import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase, SUPABASE_URL } from '../lib/supabase';
import { Lock, Link, BarChart3, Mic, Activity, FileText, Cpu, AlertTriangle, Database, RefreshCw, KeyRound, BookOpen, UserPlus, Copy, Eye, Ban, Rocket } from 'lucide-react';
import { toast } from 'sonner';


const DEVELOPER_CODE = '240501';

const Developer = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const isDark = theme === 'dark';
  const [code, setCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [embedStats, setEmbedStats] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Prospect onboarding invites
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteCompany, setInviteCompany] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDays, setInviteDays] = useState(14);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({}); // invite_id -> key
  const [startingTrial, setStartingTrial] = useState(null); // invite_id
  const [connected, setConnected] = useState([]);
  const [revealedOrgKeys, setRevealedOrgKeys] = useState({}); // org_id -> key

  const callOnboard = async (payload) => {
    const session = (await supabase.auth.getSession()).data.session;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/onboard-intake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
      body: JSON.stringify(payload),
    });
    return res.json();
  };

  const loadConnected = async () => {
    try {
      const res = await callOnboard({ action: 'list_connected' });
      if (res.ok) setConnected(res.organizations || []);
    } catch (_) { /* staff-only; ignore */ }
  };

  const revealOrgKey = async (org) => {
    if (revealedOrgKeys[org.id]) {
      setRevealedOrgKeys(prev => { const n = { ...prev }; delete n[org.id]; return n; });
      return;
    }
    const res = await callOnboard({ action: 'reveal_org_key', organization_id: org.id });
    if (res.ok) setRevealedOrgKeys(prev => ({ ...prev, [org.id]: res.api_key }));
    else toast.error(res.error || 'Failed to reveal key');
  };

  const loadInvites = async () => {
    loadConnected();
    setInvitesLoading(true);
    try {
      const res = await callOnboard({ action: 'list_invites' });
      if (res.ok) setInvites(res.invites || []);
      else toast.error(res.error || 'Failed to load invites');
    } catch (e) {
      toast.error(e?.message || 'Failed to load invites');
    }
    setInvitesLoading(false);
  };

  const createInvite = async (e) => {
    e.preventDefault();
    if (!inviteCompany.trim()) return;
    setCreatingInvite(true);
    try {
      const res = await callOnboard({
        action: 'create_invite',
        company_name: inviteCompany.trim(),
        contact_email: inviteEmail.trim() || null,
        expires_days: Number(inviteDays) || 14,
      });
      if (res.ok) {
        await navigator.clipboard.writeText(res.link).catch(() => {});
        toast.success('Invite created — link copied to clipboard');
        setInviteCompany('');
        setInviteEmail('');
        loadInvites();
      } else {
        toast.error(res.error || 'Failed to create invite');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to create invite');
    }
    setCreatingInvite(false);
  };

  const copyInviteLink = async (invite) => {
    await navigator.clipboard.writeText(invite.link).catch(() => {});
    toast.success('Link copied');
  };

  const revealKey = async (invite) => {
    if (revealedKeys[invite.id]) {
      setRevealedKeys(prev => { const n = { ...prev }; delete n[invite.id]; return n; });
      return;
    }
    try {
      const res = await callOnboard({ action: 'reveal_key', invite_id: invite.id });
      if (res.ok) {
        setRevealedKeys(prev => ({ ...prev, [invite.id]: res.api_key }));
      } else {
        toast.error(res.error || 'Failed to reveal key');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to reveal key');
    }
  };

  const revokeInvite = async (invite) => {
    if (!window.confirm(`Revoke the onboarding link for "${invite.company_name}"? The link will stop working immediately.`)) return;
    const res = await callOnboard({ action: 'revoke_invite', invite_id: invite.id });
    if (res.ok) { toast.success('Invite revoked'); loadInvites(); }
    else toast.error(res.error || 'Failed to revoke');
  };

  const startTrial = async (invite) => {
    const email = window.prompt(
      `Start a free trial for "${invite.company_name}"?\n\n` +
      `This creates their TutorTracer account, imports their last 7 days of lessons and keeps syncing for 14 days or 400 lesson hours, whichever comes first.\n\n` +
      `A login invite email will be sent to this address (edit if needed):`,
      invite.submitter_email || ''
    );
    if (!email) return;
    setStartingTrial(invite.id);
    try {
      const res = await callOnboard({ action: 'start_trial', invite_id: invite.id, admin_email: email.trim() });
      if (res.ok) {
        toast.success(res.invited ? `Trial started. Login invite sent to ${res.admin_email}` : `Trial started. ${res.admin_email} already had a login and was added`);
        loadInvites();
      } else toast.error(res.error || 'Failed to start trial');
    } finally {
      setStartingTrial(null);
    }
  };

  const checkEmbeddings = async () => {
    if (!organizationId) return;
    setEmbedLoading(true);
    setEmbedError('');
    try {
      const [{ count: processed }, { count: docs, error: docErr }] = await Promise.all([
        supabase.from('sessions').select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId).eq('is_processed_v2', true),
        supabase.from('lesson_search_documents').select('session_id', { count: 'exact', head: true })
          .eq('organization_id', organizationId),
      ]);
      if (docErr) throw docErr;
      setEmbedStats({
        processed: processed || 0,
        embedded: docs || 0,
        missing: Math.max(0, (processed || 0) - (docs || 0)),
      });
    } catch (e) {
      setEmbedError(e.message || 'Failed to read counts. The lesson_search_documents table may not be deployed.');
    }
    setEmbedLoading(false);
  };

  const runBackfill = async () => {
    if (!organizationId) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/backfill-embeddings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ organization_id: organizationId, limit: 200, batch_size: 25 }),
      });
      const data = await res.json();
      setBackfillResult(data);
      await checkEmbeddings();
    } catch (e) {
      setBackfillResult({ error: e.message || 'Backfill request failed' });
    }
    setBackfilling(false);
  };

  useEffect(() => {
    if (isAuthenticated && organizationId) checkEmbeddings();
    if (isAuthenticated) loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, organizationId]);

  useEffect(() => {
    if (localStorage.getItem('developer_authenticated') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === DEVELOPER_CODE) {
      setIsAuthenticated(true);
      localStorage.setItem('developer_authenticated', 'true');
      setError('');
    } else {
      setError('Invalid developer code');
      setCode('');
    }
  };

  const cardClass = `rounded-lg border p-5 cursor-pointer transition-colors group ${isDark ? 'bg-[#121212] border-[#27272a] hover:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] hover:border-[#d0d0d0]'}`;

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className={`rounded-lg border p-6 max-w-md w-full text-center ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <Lock className={`w-8 h-8 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
          </div>
          <h1 className={`text-xl font-semibold font-[Manrope] mb-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Developer Access</h1>
          <p className={`text-sm mb-6 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            This section is restricted to TutorTracer developers only.
          </p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="password"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="Developer code"
              className={`w-full px-4 py-2 text-sm border rounded-lg text-center ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
            <button
              type="submit"
              className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
            >
              Enter
            </button>
          </form>
          <button
            onClick={() => window.location.href = 'mailto:support@tutortracer.com?subject=Developer Access Question'}
            className={`mt-4 text-xs ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`}
          >
            Contact support for access
          </button>
        </div>
      </div>
    );
  }

  const cards = [
    { icon: KeyRound, label: 'API Access', desc: 'Manage API keys and webhook endpoints for the public API.', path: '/developer/api' },
    { icon: BookOpen, label: 'API Documentation', desc: 'Endpoint reference, webhooks, and signature verification.', path: '/developer/api-docs' },
    { icon: BarChart3, label: 'API Statistics', desc: 'Token usage, cost tracking, and processing pipeline stats.', path: '/developer/statistics' },
    { icon: Mic, label: 'Speaker ID Diagnostics', desc: 'Debug LLM speaker identification vs booking data.', path: '/developer/speaker-id' },
    { icon: Activity, label: 'Gemini Usage Logs', desc: 'Real-time API call tracking with cost estimation.', path: '/developer/gemini-logs' },
    { icon: FileText, label: 'Transcripts', desc: 'View raw transcripts and processing status.', path: '/developer/transcripts' },
    { icon: Cpu, label: 'Processed Sessions', desc: 'Review analyzed session data and analysis JSON.', path: '/developer/processed' },
    { icon: Link, label: 'Legacy App', desc: 'Open dev.tutortracer.com — full settings, integrations, and sync.', external: 'https://dev.tutortracer.com' },
  ];

  return (
    <div className="space-y-6">
      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Developer
      </h1>

      {/* Warning */}
      <div className={`text-xs px-4 py-2 rounded-lg flex items-center gap-2 ${isDark ? 'bg-amber-400/5 text-amber-400/70 border border-amber-400/10' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        These tools access production data. Use with caution.
      </div>

      {/* Assistant semantic-search diagnostics */}
      <div className={`rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <Database className={`w-5 h-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Assistant Embedding Diagnostics</h3>
            <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              Counts of processed sessions vs rows in <code className="text-[11px]">lesson_search_documents</code>. If embeddings are missing, semantic search will silently return nothing.
            </p>
          </div>
          <button
            onClick={checkEmbeddings}
            disabled={embedLoading}
            className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'} disabled:opacity-50`}
          >
            <RefreshCw className={`h-3 w-3 ${embedLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {embedError && (
          <p className="text-xs text-red-500 mb-2">{embedError}</p>
        )}

        {embedStats && (
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className={`p-3 rounded border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
              <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Processed sessions</div>
              <div className={`text-lg font-semibold mt-0.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{embedStats.processed}</div>
            </div>
            <div className={`p-3 rounded border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
              <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Embedded</div>
              <div className={`text-lg font-semibold mt-0.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{embedStats.embedded}</div>
            </div>
            <div className={`p-3 rounded border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
              <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Missing</div>
              <div className={`text-lg font-semibold mt-0.5 ${embedStats.missing > 0 ? 'text-amber-500' : isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{embedStats.missing}</div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={runBackfill}
            disabled={backfilling || !organizationId}
            className={`text-xs px-3 py-1.5 rounded-md font-medium ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'} disabled:opacity-50`}
          >
            {backfilling ? 'Running...' : 'Run backfill (up to 200 sessions)'}
          </button>
          {backfillResult && (
            <span className={`text-xs ${backfillResult.error ? 'text-red-500' : isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              {backfillResult.error
                ? `Error: ${backfillResult.error}`
                : `Embedded ${backfillResult.embedded ?? 0}, ${backfillResult.remaining ?? 0} remaining. Repeat until 0.`}
            </span>
          )}
        </div>

        {/* Manual SQL fallback for the user if the above buttons fail. */}
        <details className="mt-3">
          <summary className={`text-[11px] cursor-pointer ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`}>
            Manual SQL (run in Supabase SQL editor)
          </summary>
          <pre className={`mt-2 p-2 rounded text-[11px] overflow-x-auto ${isDark ? 'bg-[#0a0a0a] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#333]'}`}>{`-- Are embeddings populated for this org?
SELECT
  (SELECT count(*) FROM sessions WHERE organization_id = '${organizationId || '<org_id>'}' AND is_processed_v2 = true) AS processed,
  (SELECT count(*) FROM lesson_search_documents WHERE organization_id = '${organizationId || '<org_id>'}') AS docs,
  (SELECT count(*) FROM lesson_search_documents WHERE organization_id = '${organizationId || '<org_id>'}' AND embedding IS NOT NULL) AS docs_with_embedding;`}</pre>
        </details>
      </div>

      {/* Prospect onboarding — secure Lessonspace key collection links */}
      <div className={`rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <UserPlus className={`w-5 h-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Prospect Onboarding</h3>
            <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              Create a secure single-use link (app.tutortracer.com/onboard/…) for a prospect to submit their Lessonspace organisation ID and API key before their account exists. Keys are stored encrypted; reveals are logged.
            </p>
          </div>
          <button
            onClick={loadInvites}
            disabled={invitesLoading}
            className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'} disabled:opacity-50`}
          >
            <RefreshCw className={`h-3 w-3 ${invitesLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <form onSubmit={createInvite} className="flex flex-wrap items-end gap-2 mb-4">
          <div className="flex-1 min-w-[180px]">
            <label className={`block text-[10px] uppercase tracking-wide mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Company</label>
            <input
              value={inviteCompany}
              onChange={e => setInviteCompany(e.target.value)}
              placeholder="e.g. PMT Education"
              className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`}
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className={`block text-[10px] uppercase tracking-wide mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Contact email (optional)</label>
            <input
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="contact@company.com"
              className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`}
            />
          </div>
          <div className="w-24">
            <label className={`block text-[10px] uppercase tracking-wide mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Expires (days)</label>
            <input
              type="number" min="1" max="90"
              value={inviteDays}
              onChange={e => setInviteDays(e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
            />
          </div>
          <button
            type="submit"
            disabled={creatingInvite || !inviteCompany.trim()}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'} disabled:opacity-50`}
          >
            {creatingInvite ? 'Creating...' : 'Create link'}
          </button>
        </form>

        {invites.length === 0 ? (
          <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No onboarding links yet.</p>
        ) : (
          <div className={`divide-y rounded-lg border ${isDark ? 'divide-[#27272a] border-[#27272a]' : 'divide-[#f0f0f0] border-[#e5e5e5]'}`}>
            {invites.map(inv => {
              const stateCls = inv.state === 'activated' ? 'bg-sky-500/10 text-sky-500'
                : inv.state === 'submitted' ? 'bg-emerald-500/10 text-emerald-500'
                : inv.state === 'pending' ? 'bg-amber-500/10 text-amber-500'
                : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]';
              return (
                <div key={inv.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{inv.company_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${stateCls}`}>{inv.state}</span>
                    <span className={`text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {inv.state === 'pending'
                        ? `expires ${new Date(inv.expires_at).toLocaleDateString()}`
                        : inv.submitted_at
                          ? `submitted ${new Date(inv.submitted_at).toLocaleDateString()}${inv.submitter_email ? ` by ${inv.submitter_email}` : ''}${inv.activated_at ? ` · trial started ${new Date(inv.activated_at).toLocaleDateString()}` : ''}`
                          : `created ${new Date(inv.created_at).toLocaleDateString()}`}
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                      {inv.state === 'pending' && (
                        <>
                          <button onClick={() => copyInviteLink(inv)} title="Copy link"
                            className={`p-1.5 rounded-md ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => revokeInvite(inv)} title="Revoke link"
                            className={`p-1.5 rounded-md ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#18181b]' : 'text-[#999] hover:text-red-500 hover:bg-[#f5f5f5]'}`}>
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {inv.state === 'submitted' && !inv.organization_id && (
                        <button onClick={() => startTrial(inv)} disabled={startingTrial === inv.id}
                          className="text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 text-white accent-gradient hover:opacity-90 disabled:opacity-60">
                          <Rocket className="h-3 w-3" />
                          {startingTrial === inv.id ? 'Starting…' : 'Start trial'}
                        </button>
                      )}
                      {(inv.state === 'submitted' || inv.state === 'activated') && (
                        <button onClick={() => revealKey(inv)} title={revealedKeys[inv.id] ? 'Hide key' : 'Reveal API key (logged)'}
                          className={`text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'}`}>
                          <Eye className="h-3 w-3" />
                          {revealedKeys[inv.id] ? 'Hide key' : 'Reveal key'}
                        </button>
                      )}
                    </div>
                  </div>
                  {(inv.state === 'submitted' || inv.state === 'activated') && (
                    <div className={`mt-1.5 text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      Lessonspace org: <span className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>{inv.lessonspace_org_id || '—'}</span>
                      {inv.key_viewed_count > 0 && <> · key viewed {inv.key_viewed_count}×, last {new Date(inv.key_viewed_at).toLocaleString()}</>}
                      {inv.notes && <> · note: {inv.notes}</>}
                    </div>
                  )}
                  {revealedKeys[inv.id] && (
                    <div className={`mt-2 flex items-center gap-2 p-2 rounded border font-mono text-xs break-all ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white' : 'bg-[#fafafa] border-[#e5e5e5] text-[#1a1a1a]'}`}>
                      <span className="flex-1">{revealedKeys[inv.id]}</span>
                      <button
                        onClick={async () => { await navigator.clipboard.writeText(revealedKeys[inv.id]).catch(() => {}); toast.success('Key copied'); }}
                        className={`p-1 rounded ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}
                        title="Copy key"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={`${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'} border rounded-xl p-5 mb-6`}>
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className={`h-4 w-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} />
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Connected Lessonspace accounts</h3>
        </div>
        <p className={`text-xs mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Every organisation with a Lessonspace key stored. Keys are encrypted at rest; each reveal is logged.</p>
        {connected.length === 0 ? (
          <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>None.</p>
        ) : (
          <div className={`divide-y rounded-lg border ${isDark ? 'divide-[#27272a] border-[#27272a]' : 'divide-[#f0f0f0] border-[#e5e5e5]'}`}>
            {connected.map(org => (
              <div key={org.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{org.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${org.lessonspace_sync_enabled ? 'bg-emerald-500/10 text-emerald-500' : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'}`}>{org.lessonspace_sync_enabled ? 'syncing' : 'sync off'}</span>
                  <span className={`text-[11px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    Lessonspace org: <span className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>{org.lessonspace_org_id || '—'}</span>
                    {org.lessonspace_last_sync_at && <> · last sync {new Date(org.lessonspace_last_sync_at).toLocaleString()}</>}
                  </span>
                  <button onClick={() => revealOrgKey(org)}
                    className={`ml-auto text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'}`}>
                    <Eye className="h-3 w-3" />
                    {revealedOrgKeys[org.id] ? 'Hide key' : 'Reveal key'}
                  </button>
                </div>
                {revealedOrgKeys[org.id] && (
                  <div className={`mt-2 flex items-center gap-2 p-2 rounded border font-mono text-xs break-all ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white' : 'bg-[#fafafa] border-[#e5e5e5] text-[#1a1a1a]'}`}>
                    <span className="flex-1">{revealedOrgKeys[org.id]}</span>
                    <button onClick={async () => { await navigator.clipboard.writeText(revealedOrgKeys[org.id]).catch(() => {}); toast.success('Key copied'); }}
                      className={`p-1 rounded ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`} title="Copy key">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(card => {
          const Tag = card.external ? 'a' : 'button';
          const props = card.external
            ? { href: card.external, target: '_blank', rel: 'noopener noreferrer' }
            : { onClick: () => navigate(card.path) };
          return (
            <Tag key={card.label} {...props} className={cardClass}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
                <card.icon className={`w-5 h-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
              </div>
              <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{card.label}</h3>
              <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{card.desc}</p>
            </Tag>
          );
        })}
      </div>
    </div>
  );
};

export default Developer;
